<template>
    <AmModal
        :open="open"
        size="large"
        eyebrow="НАСТРОЙКА ТАРИФА"
        accent-eyebrow
        :title="modalTitle"
        :subtitle="modalSubtitle"
        @close="$emit('close')"
    >
        <div v-if="steps.length" class="steps" role="tablist" aria-label="Шаги настройки">
            <button
                v-for="(s, idx) in steps"
                :key="s.key"
                type="button"
                class="step"
                :class="{
                    'step--active': stepKey === s.key,
                    'step--done': stepIndex > idx,
                }"
                role="tab"
                :aria-selected="stepKey === s.key"
                @click="goStep(idx)"
            >
                <span class="step__num">{{ idx + 1 }}</span>
                <span class="step__label">{{ s.label }}</span>
            </button>
        </div>

        <div v-if="loading" class="state">Загружаем настройки тарифа…</div>
        <div v-else-if="error" class="state state--error">{{ error }}</div>

        <template v-else>
            <!-- Выбор источников -->
            <section v-show="stepKey === 'sources'" class="panel">
                <div v-if="includedSources.length" class="included">
                    <div class="included__title">Уже включены в тариф</div>
                    <ul class="chips">
                        <li v-for="src in includedSources" :key="src.code" class="chip chip--fixed">
                            {{ src.name || src.code }}
                        </li>
                    </ul>
                </div>

                <template v-if="sourceOptions.length">
                    <p class="panel__lead">
                        Выберите дополнительные источники из доступных по тарифу.
                        <template v-if="sourcesLimit > 0">Лимит: до {{ sourcesLimit }}.</template>
                    </p>
                    <AmMultiSelect
                        v-model="sourcesGlobal"
                        :options="sourceOptions"
                        :limit="sourcesLimit"
                        placeholder="Выберите источники…"
                    />
                </template>
                <p v-else class="banner banner--info">
                    Дополнительных источников на выбор в этом тарифе нет — всё уже включено.
                </p>
            </section>

            <!-- Регионы: глобально + override по выбранным источникам -->
            <section v-show="stepKey === 'regions'" class="panel">
                <p class="panel__lead">
                    Выберите регионы по умолчанию для источников тарифа.
                    <template v-if="regionsLimit > 0">Лимит: до {{ regionsLimit }}.</template>
                </p>
                <AmMultiSelect
                    v-model="regionsGlobal"
                    :options="regionOptions"
                    :limit="regionsLimit"
                    placeholder="Начните вводить название региона…"
                />
                <p v-if="regionOptions.length === 0" class="banner banner--warn">
                    Справочник регионов пуст. Обратитесь к администратору или запустите синхронизацию регионов.
                </p>

                <template v-if="regionsGlobal.length && sourcesForRegionOverrides.length">
                    <p class="panel__lead" style="margin-top: 8px">
                        Проверьте доступность регионов по источникам. При необходимости задайте отдельный набор.
                    </p>
                    <article
                        v-for="src in sourcesForRegionOverrides"
                        :key="src.code"
                        class="source-card"
                    >
                        <header class="source-card__head">
                            <div>
                                <strong>{{ src.name || src.code }}</strong>
                            </div>
                            <label class="override-toggle">
                                <input v-model="overrideEnabled[src.code]" type="checkbox" />
                                <span>Свой набор</span>
                            </label>
                        </header>

                        <div v-if="!src.has_region_catalog" class="banner banner--info">
                            У источника нет справочника регионов — выбранные регионы передаются как есть.
                        </div>

                        <ul class="matrix" v-if="regionsGlobal.length">
                            <li
                                v-for="code in regionsGlobal"
                                :key="code"
                                class="matrix__item"
                                :class="{
                                    'matrix__item--ok': matrix[code]?.[src.code] !== false,
                                    'matrix__item--miss': matrix[code]?.[src.code] === false,
                                }"
                            >
                                <span>{{ regionName(code) }}</span>
                                <span class="matrix__badge">
                                    {{ matrix[code]?.[src.code] === false ? 'нет в источнике' : 'есть' }}
                                </span>
                            </li>
                        </ul>

                        <AmMultiSelect
                            v-if="overrideEnabled[src.code]"
                            v-model="sourceOverrides[src.code]"
                            :options="optionsForSource(src)"
                            :limit="regionsLimit"
                            placeholder="Регионы только для этого источника…"
                        />
                    </article>
                </template>
            </section>

            <!-- Сводка -->
            <section v-show="stepKey === 'summary'" class="panel">
                <p class="panel__lead">Проверьте настройки перед оплатой.</p>
                <div class="summary">
                    <div class="summary__row">
                        <span class="muted">Тариф</span>
                        <strong>{{ planName }}</strong>
                    </div>
                    <div class="summary__row">
                        <span class="muted">Период</span>
                        <strong>{{ billingPeriod === 'year' ? 'Год' : 'Месяц' }}</strong>
                    </div>
                    <div v-if="includedSources.length" class="summary__row">
                        <span class="muted">Источники тарифа</span>
                        <strong>{{ includedSources.map(s => s.name || s.code).join(', ') }}</strong>
                    </div>
                    <div v-if="sourceSelectionEnabled && sourceOptions.length" class="summary__row">
                        <span class="muted">Выбранные источники</span>
                        <strong>{{ sourcesGlobal.length ? sourceNames(sourcesGlobal).join(', ') : 'не выбраны' }}</strong>
                    </div>
                    <div v-if="regionSelectionEnabled" class="summary__row">
                        <span class="muted">Регионы (глобально)</span>
                        <strong>{{ regionsGlobal.length ? regionNames(regionsGlobal).join(', ') : 'не выбраны' }}</strong>
                    </div>
                    <div
                        v-for="src in sourcesWithOverride"
                        :key="src.code"
                        class="summary__row"
                    >
                        <span class="muted">{{ src.name }}</span>
                        <strong>{{ regionNames(sourceOverrides[src.code] || []).join(', ') || '—' }}</strong>
                    </div>
                </div>

                <div v-if="missingHints.length" class="banner banner--warn">
                    <p>Внимание: часть регионов отсутствует в источниках:</p>
                    <ul>
                        <li v-for="h in missingHints" :key="h">{{ h }}</li>
                    </ul>
                </div>

                <p v-if="submitError" class="state state--error">{{ submitError }}</p>
            </section>
        </template>

        <template #footer>
            <div class="footer">
                <button type="button" class="btn btn--ghost" @click="onBack">
                    {{ stepIndex === 0 ? 'Отмена' : 'Назад' }}
                </button>
                <button
                    v-if="!isLastStep"
                    type="button"
                    class="btn btn--primary"
                    :disabled="loading || !!error || !canProceed"
                    @click="stepIndex += 1"
                >
                    Далее
                </button>
                <button
                    v-else
                    type="button"
                    class="btn btn--primary"
                    :disabled="submitting || !canSubmit"
                    @click="submit"
                >
                    {{ submitting ? 'Создаём заказ…' : 'К оплате' }}
                </button>
            </div>
        </template>
    </AmModal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import AmMultiSelect from '@/components/ui/AmMultiSelect.vue'
import { getChatRegionsConfigPreview, getChatSourcesConfigPreview } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    planCode: { type: String, default: '' },
    planName: { type: String, default: '' },
    billingPeriod: { type: String, default: 'month' },
    regionSelectionEnabled: { type: Boolean, default: false },
    sourceSelectionEnabled: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'confirm'])

const stepIndex = ref(0)
const loading = ref(false)
const error = ref('')
const submitting = ref(false)
const submitError = ref('')

const allRegions = ref([])
const availableSources = ref([])
const includedSources = ref([])
const regionSources = ref([])
const matrix = ref({})
const regionsLimit = ref(0)
const sourcesLimit = ref(0)

const regionsGlobal = ref([])
const sourcesGlobal = ref([])
const overrideEnabled = reactive({})
const sourceOverrides = reactive({})

const steps = computed(() => {
    const list = []
    if (props.sourceSelectionEnabled) list.push({ key: 'sources', label: 'Источники' })
    if (props.regionSelectionEnabled) list.push({ key: 'regions', label: 'Регионы' })
    list.push({ key: 'summary', label: 'Оплата' })
    return list
})

const stepKey = computed(() => steps.value[stepIndex.value]?.key || 'summary')
const isLastStep = computed(() => stepIndex.value >= steps.value.length - 1)

const modalTitle = computed(() => {
    if (stepKey.value === 'sources') return 'Выберите источники'
    if (stepKey.value === 'regions') return 'Выберите регионы'
    return 'Подтверждение'
})

const modalSubtitle = computed(() => {
    if (!props.planName) return ''
    return `Тариф «${props.planName}» · ${props.billingPeriod === 'year' ? 'оплата за год' : 'оплата за месяц'}`
})

const regionOptions = computed(() =>
    allRegions.value.map(r => ({ value: r.code, label: r.name })),
)

const sourceOptions = computed(() =>
    availableSources.value.map(s => ({ value: s.code, label: s.name || s.code })),
)

/**
 * Источники для override регионов: включённые в тариф + выбранные пользователем.
 * Если выбор источников не настроен — весь пул тарифа.
 */
const sourcesForRegionOverrides = computed(() => {
    if (!props.sourceSelectionEnabled || sourceOptions.value.length === 0) {
        return regionSources.value
    }
    const active = new Set([
        ...includedSources.value.map(s => String(s.code)),
        ...sourcesGlobal.value.map(String),
    ])
    return regionSources.value.filter(s => active.has(String(s.code)))
})

const sourcesWithOverride = computed(() =>
    sourcesForRegionOverrides.value.filter(s => overrideEnabled[s.code]),
)

const missingHints = computed(() => {
    const hints = []
    for (const code of regionsGlobal.value) {
        for (const src of sourcesForRegionOverrides.value) {
            if (!src.has_region_catalog) continue
            if (matrix.value[code]?.[src.code] === false) {
                hints.push(`${regionName(code)} — нет в «${src.name}»`)
            }
        }
    }
    return hints.slice(0, 8)
})

/** Выбор источников обязателен только если у тарифа есть источники-опции. */
const sourceChoiceRequired = computed(() =>
    props.sourceSelectionEnabled && sourceOptions.value.length > 0,
)

const canProceed = computed(() => {
    if (stepKey.value === 'sources') return !sourceChoiceRequired.value || sourcesGlobal.value.length > 0
    if (stepKey.value === 'regions') return regionsGlobal.value.length > 0
    return true
})

const canSubmit = computed(() => {
    if (sourceChoiceRequired.value && sourcesGlobal.value.length === 0) return false
    if (props.regionSelectionEnabled && regionsGlobal.value.length === 0) return false
    return true
})

watch(
    () => props.open,
    async (open) => {
        if (!open) return
        stepIndex.value = 0
        submitError.value = ''
        regionsGlobal.value = []
        sourcesGlobal.value = []
        Object.keys(overrideEnabled).forEach(k => delete overrideEnabled[k])
        Object.keys(sourceOverrides).forEach(k => delete sourceOverrides[k])
        await loadPreview()
    },
)

async function loadPreview() {
    if (!props.planCode) return
    loading.value = true
    error.value = ''
    try {
        const tasks = []
        if (props.regionSelectionEnabled) {
            tasks.push(getChatRegionsConfigPreview(props.planCode).then(data => {
                allRegions.value = data?.all_regions || []
                regionSources.value = data?.sources || []
                matrix.value = data?.matrix || {}
                regionsLimit.value = Number(data?.plan?.regions_limit || 0)
                for (const src of regionSources.value) {
                    overrideEnabled[src.code] = false
                    sourceOverrides[src.code] = []
                }
            }))
        }
        if (props.sourceSelectionEnabled) {
            tasks.push(getChatSourcesConfigPreview(props.planCode).then(data => {
                availableSources.value = data?.available_sources || []
                includedSources.value = data?.included_sources || []
                sourcesLimit.value = Number(data?.plan?.sources_limit || 0)
            }))
        }
        await Promise.all(tasks)
    } catch (e) {
        error.value = e?.message || 'Не удалось загрузить настройки тарифа'
    } finally {
        loading.value = false
    }
}

function regionName(code) {
    return allRegions.value.find(r => r.code === code)?.name || code
}

function regionNames(codes) {
    return codes.map(regionName)
}

function sourceName(code) {
    return availableSources.value.find(s => s.code === code)?.name
        || includedSources.value.find(s => s.code === code)?.name
        || regionSources.value.find(s => s.code === code)?.name
        || code
}

function sourceNames(codes) {
    return codes.map(sourceName)
}

function optionsForSource(src) {
    const list = src.available_regions?.length
        ? src.available_regions
        : allRegions.value
    return list.map(r => ({ value: r.code, label: r.name }))
}

function goStep(idx) {
    if (idx < stepIndex.value) stepIndex.value = idx
}

function onBack() {
    if (stepIndex.value === 0) {
        emit('close')
        return
    }
    stepIndex.value -= 1
}

function buildConfig() {
    const config = {}
    if (sourceChoiceRequired.value) {
        config.sources_global = [...sourcesGlobal.value]
    }
    if (props.regionSelectionEnabled) {
        const source_overrides = {}
        for (const src of sourcesForRegionOverrides.value) {
            if (!overrideEnabled[src.code]) continue
            const regions = sourceOverrides[src.code] || []
            if (regions.length) {
                source_overrides[src.code] = { regions: [...regions] }
            }
        }
        config.regions_global = [...regionsGlobal.value]
        config.source_overrides = source_overrides
    }
    return config
}

function submit() {
    submitError.value = ''
    if (sourceChoiceRequired.value && sourcesGlobal.value.length === 0) {
        submitError.value = 'Выберите хотя бы один источник'
        return
    }
    if (props.regionSelectionEnabled && regionsGlobal.value.length === 0) {
        submitError.value = 'Выберите хотя бы один регион'
        return
    }
    submitting.value = true
    try {
        emit('confirm', {
            code: props.planCode,
            billingPeriod: props.billingPeriod,
            config: buildConfig(),
        })
    } finally {
        submitting.value = false
    }
}
</script>

<style scoped>
.steps {
    display: flex;
    gap: 8px;
    margin-bottom: 18px;
}
.step {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border: 1px solid #e7e7e7;
    border-radius: 10px;
    background: #fafbfc;
    font-family: inherit;
    cursor: pointer;
    color: #5f5f5f;
}
.step--active {
    border-color: #E30613;
    background: #FFF5F5;
    color: #23282C;
}
.step--done {
    border-color: #198FA0;
    color: #198FA0;
}
.step__num {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #e7e7e7;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
}
.step--active .step__num {
    background: #E30613;
    color: white;
}
.step--done .step__num {
    background: #198FA0;
    color: white;
}
.step__label {
    font-size: 12.5px;
    font-weight: 600;
}

.panel { display: flex; flex-direction: column; gap: 14px; }
.panel__lead { margin: 0; font-size: 13.5px; color: #5f5f5f; line-height: 1.45; }

.state { padding: 24px; text-align: center; color: #5f5f5f; font-size: 13.5px; }
.state--error { color: #C0050F; background: #FFEAE7; border-radius: 10px; }

.source-card {
    border: 1px solid #e7e7e7;
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.source-card__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
}
.source-card__head strong { display: block; font-size: 14px; color: #23282C; }
.muted { font-size: 11.5px; color: #888; font-style: normal; }

.override-toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12.5px;
    color: #23282C;
    cursor: pointer;
    white-space: nowrap;
}

.matrix {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.matrix__item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 999px;
    font-size: 12px;
    border: 1px solid #e7e7e7;
}
.matrix__item--ok { background: #EAF8F5; border-color: #B8E5DB; color: #0F6B5C; }
.matrix__item--miss { background: #FFF5E8; border-color: #F5D5A6; color: #8A5A00; }
.matrix__badge { font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.3px; opacity: 0.85; }

.banner {
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 12.5px;
    line-height: 1.4;
}
.included {
    border: 1px solid #e7e7e7;
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}
.included__title {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #888;
}
.chips {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.chip {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 12.5px;
    border: 1px solid #e7e7e7;
}
.chip--fixed { background: #EAF8F5; border-color: #B8E5DB; color: #0F6B5C; }

.banner--info { background: #EEF6FA; color: #1A5F73; }
.banner--warn { background: #FFF5E8; color: #8A5A00; }
.banner ul { margin: 6px 0 0; padding-left: 18px; }

.summary {
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid #e7e7e7;
    border-radius: 12px;
    padding: 14px;
}
.summary__row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    font-size: 13.5px;
    color: #23282C;
}
.summary__row strong { text-align: right; font-weight: 600; }

.footer {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
}
.btn {
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 10px 18px;
    border-radius: 10px;
    border: 0;
    cursor: pointer;
}
.btn--ghost {
    background: white;
    border: 1px solid #e0e0e0;
    color: #23282C;
}
.btn--ghost:hover { border-color: #23282C; }
.btn--primary {
    background: #E30613;
    color: white;
}
.btn--primary:hover { background: #C0050F; }
.btn--primary:disabled,
.btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
</style>
